import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { authHeaders, errorMessage, unwrap } from 'api/call'
import {
  getClubManagerProfiles,
  putClubManagerProfilesByIdApprove,
  putClubManagerProfilesByIdReject,
  type ClubManagerProfileResponse,
  type ClubManagerProfilesIndexResponse,
} from 'api/generated'

type ClubManagerProfile =
  ClubManagerProfilesIndexResponse['club_manager_profiles'][number]

export type ClubManagerRow = ClubManagerProfile & {
  loaders: { approval: boolean }
}

interface ClubManagersState {
  loading: boolean
  error: string | null
  clubManagers: ClubManagerRow[] | null
  page: number
  totalPages: number
}

const initialState: ClubManagersState = {
  loading: false,
  error: null,
  clubManagers: null,
  page: 1,
  totalPages: 0,
}

type DecisionArg = { clubManagerProfileId: number; notes: string }

export const fetchClubManagers = createAsyncThunk<
  ClubManagerProfilesIndexResponse,
  number,
  { rejectValue: string }
>('clubManagers/fetchClubManagers', async (page, { rejectWithValue }) => {
  try {
    return await unwrap(
      getClubManagerProfiles({ headers: authHeaders(), query: { page } })
    )
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Failed to fetch club managers.')
    )
  }
})

export const approveClubManager = createAsyncThunk<
  ClubManagerProfileResponse,
  DecisionArg,
  { rejectValue: string }
>(
  'clubManagers/approve',
  async ({ clubManagerProfileId, notes }, { rejectWithValue }) => {
    try {
      return await unwrap(
        putClubManagerProfilesByIdApprove({
          headers: authHeaders(),
          path: { id: clubManagerProfileId },
          body: { notes },
        })
      )
    } catch (error) {
      return rejectWithValue(
        errorMessage(error, 'Failed to approve club manager.')
      )
    }
  }
)

export const rejectClubManager = createAsyncThunk<
  ClubManagerProfileResponse,
  DecisionArg,
  { rejectValue: string }
>(
  'clubManagers/reject',
  async ({ clubManagerProfileId, notes }, { rejectWithValue }) => {
    try {
      return await unwrap(
        putClubManagerProfilesByIdReject({
          headers: authHeaders(),
          path: { id: clubManagerProfileId },
          body: { notes },
        })
      )
    } catch (error) {
      return rejectWithValue(
        errorMessage(error, 'Failed to reject club manager.')
      )
    }
  }
)

const setApprovalLoader = (
  state: ClubManagersState,
  id: number,
  approval: boolean
) => {
  state.clubManagers =
    state.clubManagers?.map((clubManager) =>
      clubManager.id === id
        ? { ...clubManager, loaders: { approval } }
        : clubManager
    ) ?? null
}

// The API returns the updated profile: take status/notes from it
const applyDecision = (
  state: ClubManagersState,
  { club_manager_profile }: ClubManagerProfileResponse
) => {
  state.clubManagers =
    state.clubManagers?.map((clubManager) =>
      clubManager.id === club_manager_profile.id
        ? { ...club_manager_profile, loaders: { approval: false } }
        : clubManager
    ) ?? null
}

const clubManagersSlice = createSlice({
  name: 'clubManagers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClubManagers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchClubManagers.fulfilled, (state, action) => {
        state.loading = false
        state.totalPages = action.payload.total_pages
        state.clubManagers = action.payload.club_manager_profiles.map(
          (clubManager) => ({ ...clubManager, loaders: { approval: false } })
        )
      })
      .addCase(fetchClubManagers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch club managers.'
      })

    for (const thunk of [approveClubManager, rejectClubManager]) {
      builder
        .addCase(thunk.pending, (state, action) => {
          setApprovalLoader(state, action.meta.arg.clubManagerProfileId, true)
          state.error = null
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading = false
          applyDecision(state, action.payload)
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false
          state.error = action.payload || 'Operazione sul club manager fallita.'
          setApprovalLoader(state, action.meta.arg.clubManagerProfileId, false)
        })
    }
  },
})

export const clubManagersReducer = clubManagersSlice.reducer
